import { clsx } from "clsx";
import { Check, ClipboardCheck, Lightbulb } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { daysUntil } from "@/components/admin/ExamDateBadge";
import { Badge, Button, Card, Input, Textarea } from "@/components/common";
import { useSetAgentOnboardingItem } from "@/hooks/useAgents";
import type {
  AgentOnboarding,
  AgentOnboardingHints,
  AgentOnboardingItem,
  OnboardingItemKey,
} from "@/types/agent";

const LABELS: Record<OnboardingItemKey, string> = {
  agency_agreement: "Umowa agencyjna podpisana",
  insurer_authorizations: "Pełnomocnictwa u TU",
  exam_passed: "Egzamin zdany",
  initial_training: "Szkolenie wstępne",
  knf_registry: "Wpis do rejestru pośredników KNF",
  panel_access: "Dostęp do panelu założony",
};

function errorMessage(error: unknown, fallback: string) {
  return (error as { message?: string } | null)?.message ?? fallback;
}

/** "YYYY-MM-DD" → "15 października 2026" */
function formatDay(day: string) {
  return new Date(`${day}T00:00:00`).toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function today() {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

/** What the panel already knows about the step, or null when it knows nothing. */
function hintFor(
  key: OnboardingItemKey,
  hints: AgentOnboardingHints,
): string | null {
  switch (key) {
    case "insurer_authorizations":
      return hints.authorizationCount > 0
        ? `Zaznaczone upoważnienia: ${hints.authorizationCount} TU.`
        : "Brak zaznaczonych upoważnień w karcie poniżej.";
    case "exam_passed": {
      if (!hints.examInsurerName) {
        return "Agent nie jest zgłoszony na egzamin.";
      }
      if (!hints.examDate) {
        return `Zgłoszony przez ${hints.examInsurerName}, termin nieznany.`;
      }
      const past = daysUntil(hints.examDate) < 0;
      return `Egzamin ${past ? "odbył się" : "jest"} ${formatDay(hints.examDate)} (${hints.examInsurerName})${past ? " — sprawdź wynik." : "."}`;
    }
    case "knf_registry":
      return hints.rauNumber
        ? `Nr RAU w profilu agenta: ${hints.rauNumber}.`
        : null;
    case "panel_access":
      if (!hints.hasPanelAccount) {
        return "Agent nie ma jeszcze konta w panelu.";
      }
      return hints.lastLoginAt
        ? "Konto w panelu istnieje, agent już się logował."
        : "Konto w panelu istnieje, agent jeszcze się nie logował.";
    default:
      return null;
  }
}

function ItemForm({
  agentId,
  item,
  hints,
  onDone,
}: {
  agentId: string;
  item: AgentOnboardingItem;
  hints: AgentOnboardingHints;
  onDone: () => void;
}) {
  const save = useSetAgentOnboardingItem(agentId);
  const isKnf = item.key === "knf_registry";
  const [completedOn, setCompletedOn] = useState(item.completedOn ?? today());
  const [note, setNote] = useState(item.note ?? "");
  const [reference, setReference] = useState(
    item.reference ?? (isKnf ? (hints.rauNumber ?? "") : ""),
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    save.mutate(
      {
        key: item.key,
        payload: {
          done: true,
          completedOn: completedOn || null,
          note: note.trim() || null,
          reference: isKnf ? reference.trim() || null : null,
        },
      },
      {
        onSuccess: () => {
          toast.success(`Odhaczono: ${LABELS[item.key]}`);
          onDone();
        },
        onError: (error) => {
          toast.error(errorMessage(error, "Nie udało się zapisać kroku"));
        },
      },
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 space-y-3 rounded-lg border border-edge-soft bg-subtle p-3"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          type="date"
          label="Data"
          value={completedOn}
          onChange={(e) => {
            setCompletedOn(e.target.value);
          }}
        />
        {isKnf && (
          <Input
            label="Numer wpisu"
            value={reference}
            maxLength={30}
            placeholder="np. 11223344/A"
            onChange={(e) => {
              setReference(e.target.value);
            }}
          />
        )}
      </div>
      <Textarea
        label="Notatka (opcjonalnie)"
        rows={2}
        maxLength={500}
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
        }}
      />
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onDone}
          disabled={save.isPending}
        >
          Anuluj
        </Button>
        <Button type="submit" size="sm" isLoading={save.isPending}>
          {item.done ? "Zapisz" : "Odhacz"}
        </Button>
      </div>
    </form>
  );
}

function ItemRow({
  agentId,
  item,
  hints,
  canUpdate,
}: {
  agentId: string;
  item: AgentOnboardingItem;
  hints: AgentOnboardingHints;
  canUpdate: boolean;
}) {
  const save = useSetAgentOnboardingItem(agentId);
  const [editing, setEditing] = useState(false);
  const hint = hintFor(item.key, hints);

  function handleToggle() {
    if (!item.done) {
      setEditing(true);
      return;
    }
    save.mutate(
      { key: item.key, payload: { done: false } },
      {
        onSuccess: () => {
          toast.success(`Cofnięto: ${LABELS[item.key]}`);
        },
        onError: (error) => {
          toast.error(errorMessage(error, "Nie udało się cofnąć kroku"));
        },
      },
    );
  }

  return (
    <li className="px-4 py-3">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          aria-label={LABELS[item.key]}
          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded accent-adara-700 disabled:cursor-default"
          checked={item.done || editing}
          disabled={!canUpdate || save.isPending}
          onChange={handleToggle}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span
              className={clsx(
                "text-sm font-medium",
                item.done ? "text-main" : "text-sub",
              )}
            >
              {LABELS[item.key]}
            </span>
            {canUpdate && item.done && !editing && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditing(true);
                }}
              >
                Edytuj
              </Button>
            )}
          </div>
          {item.done && item.completedOn && (
            <p className="text-xs text-dim">
              {formatDay(item.completedOn)}
              {item.completedByName && ` · odhaczył(a) ${item.completedByName}`}
              {item.reference && (
                <>
                  {" · "}nr wpisu{" "}
                  <span className="font-medium text-sub">{item.reference}</span>
                </>
              )}
            </p>
          )}
          {item.done && item.note && (
            <p className="mt-1 text-sm text-sub">{item.note}</p>
          )}
          {!item.done && hint && (
            <p className="mt-0.5 inline-flex items-start gap-1 text-xs text-dim">
              <Lightbulb
                size={12}
                className="mt-0.5 shrink-0 text-amber-500"
                aria-hidden
              />
              {hint}
            </p>
          )}
          {editing && (
            <ItemForm
              agentId={agentId}
              item={item}
              hints={hints}
              onDone={() => {
                setEditing(false);
              }}
            />
          )}
        </div>
      </div>
    </li>
  );
}

export function AgentOnboardingCard({
  onboarding,
  canUpdate,
}: {
  onboarding: AgentOnboarding;
  canUpdate: boolean;
}) {
  const { doneCount, totalCount } = onboarding;
  const complete = doneCount === totalCount;
  const percent = totalCount === 0 ? 0 : (doneCount / totalCount) * 100;

  return (
    <Card padding="none">
      <div className="space-y-3 border-b border-edge-soft px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ClipboardCheck size={18} className="text-adara-700" aria-hidden />
            <h2 className="text-sm font-semibold text-main">
              Lista kontrolna nowego agenta
            </h2>
          </div>
          <Badge
            variant={complete ? "success" : "warning"}
            className="whitespace-nowrap"
          >
            {complete && <Check size={12} className="mr-1" aria-hidden />}
            {doneCount}/{totalCount}
          </Badge>
        </div>
        <div
          className="h-2 overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-label="Postęp wdrożenia"
          aria-valuemin={0}
          aria-valuemax={totalCount}
          aria-valuenow={doneCount}
        >
          <div
            className={clsx(
              "h-full rounded-full transition-all",
              complete ? "bg-green-500" : "bg-adara-600",
            )}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
      <ul className="divide-y divide-edge-soft">
        {onboarding.items.map((item) => (
          <ItemRow
            key={item.key}
            agentId={onboarding.agentId}
            item={item}
            hints={onboarding.hints}
            canUpdate={canUpdate}
          />
        ))}
      </ul>
    </Card>
  );
}
